import { useQuery } from "@tanstack/react-query";
import { contactApi, contactQueryKeys } from "service/crm/api/contact";

interface UseQueryOptions {
  enabled?: boolean;
}

// Step 1 alone: contactKey -> uuid
export const useResolveContactUuid = (
  contactKey: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: contactQueryKeys.resolve(contactKey),
    queryFn: () => contactApi.resolveContactUuidByKey(contactKey),
    enabled: (options.enabled ?? true) && !!contactKey,
  });
};

// Step 2 alone: uuid -> full contact detail
export const useContactDetails = (
  contactUuid: string,
  options: UseQueryOptions = {},
) => {
  return useQuery({
    queryKey: contactQueryKeys.detail(contactUuid),
    queryFn: () => contactApi.getContact(contactUuid),
    enabled: (options.enabled ?? true) && !!contactUuid,
  });
};

// Composed: contactKey -> uuid -> full contact detail
// This is what the detail page should use.
export const useContactDetailsByKey = (contactKey: string) => {
  const {
    data: uuid,
    isLoading: isResolving,
    isError: isResolveError,
  } = useResolveContactUuid(contactKey);

  const {
    data: contact,
    isLoading: isLoadingDetail,
    isError: isDetailError,
  } = useContactDetails(uuid ?? "", { enabled: !!uuid });

  return {
    contact,
    isLoading: isResolving || isLoadingDetail,
    isError: isResolveError || isDetailError,
  };
};

